import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BizError } from '@zopup/zop-types';

export const globalErrorInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      // Pass through successful responses unchanged
      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      // Transform HTTP errors into standardized WebApiResponse format
      const standardizedResponse = createStandardizedErrorResponse(error, req);
      
      // Log the error for debugging
      console.error('HTTP Error intercepted:', {
        url: req.url,
        method: req.method,
        status: error.status,
        error: standardizedResponse
      });

      // Return the standardized error response as a successful HTTP response
      // This way, the calling code receives a proper response object instead of an error
      return of(new HttpResponse({
        body: standardizedResponse,
        status: 200, // Return as successful HTTP response
        statusText: 'OK',
        headers: error.headers,
        url: req.url || undefined
      }));
    })
  );
};

function createStandardizedErrorResponse(error: HttpErrorResponse, req: HttpRequest<any>): { isSuccess: false; result: null; errors: Array<BizError> } {
  // Create a standardized error response format that matches your WebApiResponse structure
  const standardizedError = {
    isSuccess: false as const,
    result: null,
    errors: extractBizErrors(error)
  };

  return standardizedError;
}

function extractBizErrors(error: HttpErrorResponse): Array<BizError> {
  const bizErrors: Array<BizError> = [];

  // Handle specific HTTP error cases
  if (error.status === 400) {
    if (error.error?.errors.length > 0){
      return error.error?.errors;
    } else {
        bizErrors.push({
          errorCode: 400,
          message: 'Bad Request',
          description: ''
        });
    }
    // Bad request - handle multiple validation errors
    // if (error.error?.['101']) {
    //   // Extract all errors from the '101' array
    //   const errorMessages = error.error['101'];
    //   if (Array.isArray(errorMessages)) {
    //     errorMessages.forEach(message => {
    //       bizErrors.push({
    //         errorCode: 101,
    //         message: 'Validation Error',
    //         description: message
    //       });
    //     });
    //   } else {
    //     bizErrors.push({
    //       errorCode: 400,
    //       message: 'Bad Request',
    //       description: errorMessages
    //     });
    //   }
    // } else {
    //   // Fallback for other 400 error formats
    //   const message = extractSingleErrorMessage(error);
    //   bizErrors.push({
    //     errorCode: 400,
    //     message: 'Bad Request',
    //     description: message || 'Bad request'
    //   });
    // }
  } else if (error.status === 404) {
    // Resource not found
    bizErrors.push({
      errorCode: 404,
      message: 'Not Found',
      description: extractSingleErrorMessage(error) || 'Resource not found'
    });
  } else if (error.status === 401) {
    // Unauthorized
    bizErrors.push({
      errorCode: 401,
      message: 'Unauthorized',
      description: 'Unauthorized access. Please login again.'
    });
  } else if (error.status === 403) {
    // Forbidden
    bizErrors.push({
      errorCode: 403,
      message: 'Forbidden',
      description: 'Access forbidden. You do not have permission to perform this action.'
    });
  } else if (error.status === 500) {
    // Internal server error
    bizErrors.push({
      errorCode: 500,
      message: 'Internal Server Error',
      description: 'Internal server error. Please try again later.'
    });
  } else if (error.status === 0) {
    // Network error
    bizErrors.push({
      errorCode: 0,
      message: 'Network Error',
      description: 'Network error. Please check your internet connection.'
    });
  } else {
    // Other HTTP errors
    bizErrors.push({
      errorCode: error.status,
      message: 'HTTP Error',
      description: extractSingleErrorMessage(error) || 'An error occurred while processing your request'
    });
  }

  return bizErrors;
}

function extractSingleErrorMessage(error: HttpErrorResponse): string | null {
  // Try different common error message locations for single error messages
  if (error.error?.message) {
    return error.error.message;
  }
  
  if (error.error?.error?.message) {
    return error.error.error.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return null;
}
